import React, { useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import { subscribeToPush } from "../../lib/webPush";

const ROW_ACTION_CLASS = "min-h-0 py-1.5 px-3 text-[13px]";

export default function PushNotificationPromptRow({ dancerId, onEnabled, onDismiss }) {
  const [busy, setBusy] = useState(false);
  if (!dancerId) return null;

  async function handleEnable() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await subscribeToPush(dancerId);
      if (result.ok) {
        toast.success("Browser notifications enabled.");
        onEnabled?.();
        return;
      }
      if (result.reason === "denied") {
        toast.error("Notifications blocked — enable them in your browser settings.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ReadableCard
      className="p-0 overflow-hidden bg-plum/5 border-plum/20"
      data-testid="push-notification-prompt-row"
    >
      <div className="p-4 min-w-0 relative pr-10">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 rounded-md2 p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center text-ink/45 hover:text-ink/70 hover:bg-white/60"
          aria-label="Dismiss for now"
          data-testid="push-notification-prompt-dismiss"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden />
        </button>
        <p className="font-ui text-[12px] font-semibold text-ink/55 inline-flex items-center gap-1.5 mb-1">
          <Bell className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden />
          Browser notifications
        </p>
        <p className="font-ui text-[14px] font-medium text-ink/80 min-w-0 break-words [overflow-wrap:anywhere]">
          Get host messages, schedule changes, and day-of RSVP reminders — even when this tab is in the background.
        </p>
      </div>
      <div className="bg-white border-t border-black/[0.08] px-4 py-3 flex flex-wrap gap-2">
        <ReadableNavButton
          type="button"
          variant="primary"
          onClick={handleEnable}
          disabled={busy}
          className={ROW_ACTION_CLASS}
          data-testid="push-notification-prompt-enable"
        >
          {busy ? "Enabling…" : "Enable notifications"}
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          onClick={onDismiss}
          disabled={busy}
          className={ROW_ACTION_CLASS}
          data-testid="push-notification-prompt-later"
        >
          Not now
        </ReadableNavButton>
      </div>
    </ReadableCard>
  );
}
